import { useEffect, useRef } from 'react'
import { useMediaQuery } from '../../../../hooks/useMediaQuery'
import { ARRIVAL_DURATION, arrivalLogoFrame } from './arrivalMotion'
import { drawRipple } from './HexRipple'

/** One pausable clock owns every arrival layer and the handoff to chat. */
export function useArrivalAnimation(onComplete: () => void) {
  const ref = useRef<HTMLDivElement>(null)
  const callback = useRef(onComplete)
  callback.current = onComplete
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const mobile = useMediaQuery('(max-width: 640px)')
  useEffect(() => {
    const element = ref.current
    const svg = element?.querySelector<SVGSVGElement>('[data-arrival-grid]')
    const logo = element?.querySelector<HTMLElement>('[data-arrival-logo]')
    if (!element || !svg || !logo) return
    let frame = 0, elapsed = 0, previous: number | null = null, visible = true, complete = false
    let width = 0, height = 0, size = 48, headerTop = 24
    function paint() {
      if (!element || !svg || !logo) return
      const pose = arrivalLogoFrame(elapsed, width, height, size, headerTop, mobile)
      drawRipple(svg, elapsed)
      logo.style.opacity = String(pose.opacity)
      logo.style.transform = `translate(${pose.x}px, ${pose.y}px) scale(${pose.scale}) rotate(${pose.rotation}deg)`
    }
    function measure() {
      if (!element || !logo) return
      width = element.clientWidth; height = element.clientHeight
      size = logo.offsetWidth
      headerTop = parseFloat(getComputedStyle(element).getPropertyValue('--arrival-header-top')) || (mobile ? 22 : 24)
      paint()
    }
    function tick(now: number) {
      frame = 0
      if (!visible || document.hidden || complete) return
      if (previous !== null) elapsed = Math.min(ARRIVAL_DURATION, elapsed + Math.min(now - previous, 80))
      previous = now
      paint()
      if (elapsed === ARRIVAL_DURATION) { complete = true; callback.current(); return }
      frame = requestAnimationFrame(tick)
    }
    function resume() {
      cancelAnimationFrame(frame); previous = null
      if (!complete && visible && !document.hidden) frame = requestAnimationFrame(tick)
    }
    measure()
    if (reducedMotion) {
      elapsed = ARRIVAL_DURATION; paint()
      const timer = window.setTimeout(() => callback.current(), 0)
      return () => window.clearTimeout(timer)
    }
    const resize = new ResizeObserver(measure)
    resize.observe(element)
    const visibility = new IntersectionObserver(([entry]) => { visible = entry?.isIntersecting ?? true; resume() })
    visibility.observe(element)
    document.addEventListener('visibilitychange', resume)
    resume()
    return () => { cancelAnimationFrame(frame); resize.disconnect(); visibility.disconnect(); document.removeEventListener('visibilitychange', resume) }
  }, [reducedMotion, mobile])
  return ref
}
